import { DateTime, FixedOffsetZone } from "luxon";
import type { ValidationProblemDetails } from "entities/common";
import type { FacilityNormalized } from "EntityTypes";
import {
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueV2Status,
  Clubhouse_LgRoundParticipationStatusV2,
  Clubhouse_LgRoundScorecardStatusV2,
  Clubhouse_LgScorecardSourceV2,
  Clubhouse_LgRoundStatusV2,
  Clubhouse_LeagueWizardScheduleMode,
  RosterTeeType,
  TeeType,
} from "../../enums";
import type {
  Clubhouse_LeagueRoundV2Dto,
  Clubhouse_RoundSetting,
  Clubhouse_LeagueOperatorDashboardStatusSummaryDto,
} from "../../types/leagues";
import type {
  Clubhouse_LeagueOperatorDetailRecentRoundDto,
  Clubhouse_LeagueV2DetailSeasonDto,
} from "../../types/leagueDetails";

/**
 * Pull human-readable error lines from an API error body. Server validation
 * messages arrive in devMessage (a server-extra, not on the typed shape) or
 * detail; a single message may list multiple issues separated by newlines.
 * Returns [] when there's nothing usable, so callers can fall back to their own
 * default text.
 */
export const extractServerErrorLines = (err: unknown): string[] => {
  const data = (
    err as { data?: ValidationProblemDetails & { devMessage?: string } }
  )?.data;
  if (!data) return [];
  // FluentValidation / ProblemDetails put field messages on `errors`
  // ({ "Field.Path": ["message", …] }); prefer those over devMessage/detail so
  // the real validation text surfaces instead of a generic retry banner.
  const fieldLines = data.errors
    ? Object.values(data.errors)
        .flat()
        .map((line) => (typeof line === "string" ? line.trim() : ""))
        .filter(Boolean)
    : [];
  if (fieldLines.length > 0) return fieldLines;
  const banner =
    data.devMessage ??
    data.detail ??
    (typeof data.title === "string" ? data.title : undefined);
  if (!banner) return [];
  return banner
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
};

/**
 * Field-level messages from a 400 `ValidationProblemDetails` body, keyed by the
 * lowercased last segment of the server's field path ("Player.Email" → "email")
 * so a form can match them to its own fields. Several messages for one field
 * join with a newline. Empty when the body carries no `errors`, so callers can
 * fall back to a banner.
 */
export const extractServerFieldErrors = (
  err: unknown,
): Record<string, string> => {
  const errors = (err as { data?: ValidationProblemDetails })?.data?.errors;
  if (!errors) return {};
  const byField: Record<string, string> = {};
  Object.entries(errors).forEach(([path, lines]) => {
    const key = path.split(".").pop()?.trim().toLowerCase();
    if (!key) return;
    const text = (Array.isArray(lines) ? lines : [lines])
      .map((line) => (typeof line === "string" ? line.trim() : ""))
      .filter(Boolean)
      .join("\n");
    if (!text) return;
    byField[key] = byField[key] ? byField[key] + "\n" + text : text;
  });
  return byField;
};

/**
 * The create/update rejection that means this facility already has a league
 * with that name. Field errors (`name` / `Name`) win; otherwise the conflict
 * message is the detail line "'{name}' is already in use at '{facility}'".
 * Returns null for every other failure so callers keep their generic banner.
 */
export const leagueNameConflictMessage = (err: unknown): string | null => {
  const data = (
    err as {
      data?: ValidationProblemDetails & { devMessage?: string };
    }
  )?.data;
  if (!data) return null;
  const nameKey = data.errors
    ? Object.keys(data.errors).find(
        (key) =>
          key === "name" ||
          key === "Name" ||
          key.toLowerCase().endsWith(".name"),
      )
    : undefined;
  if (nameKey) {
    const text = (data.errors?.[nameKey] ?? [])
      .map((line) => (typeof line === "string" ? line.trim() : ""))
      .filter(Boolean)
      .join("\n");
    if (text) return text;
  }
  return (
    (data.devMessage ?? data.detail ?? "")
      .split("\n")
      .map((line) => line.trim())
      .find((line) => /already in use/i.test(line)) ?? null
  );
};

/**
 * The participating Clubhouse facility ids to send on create/update. When the
 * league spans multiple facilities (ANY_OF_MY_FACILITIES) this is every one of
 * the operator's `clubhouseFacilityId` values so the backend knows where it can
 * be played; for the single-facility default it's undefined. Facilities without
 * a `clubhouseFacilityId` are skipped.
 */
export const participatingClubhouseFacilityIds = (
  scope: Clubhouse_LeagueFacilityScope | undefined,
  facilities: FacilityNormalized[],
): number[] | undefined =>
  scope === Clubhouse_LeagueFacilityScope.ANY_OF_MY_FACILITIES
    ? facilities
        .map((f) => f.clubhouseFacilityId)
        .filter((id): id is number => id != null)
    : undefined;

/**
 * True when a multi-facility league can't be safely sent yet. The
 * `useFacilitiesByUserId` selector reads the merged entities cache, so its list
 * can be non-empty (e.g. just the current facility) while the fetch is still in
 * flight — sending then would persist an incomplete participating list. Callers
 * must pass `facilitiesLoaded` (the query's `isFinished`) so we block on both an
 * unfinished fetch and a finished-but-empty result, and ask the operator to
 * retry rather than create/update a spanning league that lists nowhere to play.
 */
export const isMissingParticipatingFacilities = (
  scope: Clubhouse_LeagueFacilityScope | undefined,
  ids: number[] | undefined,
  facilitiesLoaded: boolean,
): boolean =>
  scope === Clubhouse_LeagueFacilityScope.ANY_OF_MY_FACILITIES &&
  (!facilitiesLoaded || (ids?.length ?? 0) === 0);

/**
 * The participating clubhouse ids to send from the advanced editor: the
 * operator's exact selection, or undefined when nothing is chosen. Sent for any
 * scope so a league at a single NON-home facility persists as that facility
 * (scope alone can't say which one — THIS_FACILITY would fall back to home).
 */
export const participatingIdsToSend = (
  ids: number[] | undefined,
): number[] | undefined => (ids?.length ? ids : undefined);

/**
 * Today's calendar date in UTC as `yyyy-MM-dd`. The league API validates start
 * dates against the UTC day (BeInFutureDate), so client-side checks must use
 * the UTC day too — not local `startOfToday()`, which lags a day behind once
 * the local clock passes UTC midnight (evening in the Americas).
 */
export const utcTodayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * First non-empty IANA zone, else the browser's current zone. Create/PATCH still
 * require `leagueStartDateTimezone` in the JSON whenever dates are sent — omitting
 * the key 400s even when the server later prefers the facility snapshot.
 */
export const resolveLeagueTimezone = (
  ...candidates: Array<string | undefined | null>
): string => {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/** Today's calendar date in the league scheduling timezone (falls back to UTC). */
export const facilityTodayIso = (timeZone?: string): string => {
  if (!timeZone) return utcTodayIso();
  const zoned = DateTime.utc().setZone(timeZone);
  return zoned.isValid ? (zoned.toISODate() ?? utcTodayIso()) : utcTodayIso();
};

/**
 * Today's calendar date as a local-midnight `Date`, for use as a date-picker
 * `minDate`. When `timeZone` is the league scheduling IANA zone, that is today
 * in the facility calendar; otherwise it is UTC today. Either way the picker
 * cannot offer a day the API would reject as already passed.
 */
export const utcTodayAsLocalDate = (timeZone?: string): Date => {
  const iso = facilityTodayIso(timeZone);
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year!, (month ?? 1) - 1, day ?? 1);
};

const FRONT_9_HOLES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const BACK_9_HOLES = [10, 11, 12, 13, 14, 15, 16, 17, 18];
const FULL_18_HOLES = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18,
];

const HOLE_LAYOUT_TOKENS: Record<string, number[]> = {
  FRONT_9: FRONT_9_HOLES,
  CUSTOM_FRONT_9: FRONT_9_HOLES,
  BACK_9: BACK_9_HOLES,
  CUSTOM_BACK_9: BACK_9_HOLES,
  FULL_18: FULL_18_HOLES,
};

/** Parse a JSON-array hole list or layout token (e.g. FRONT_9) into numbers. */
export const parseSelectedHolesJson = (
  json: string | undefined,
): number[] | undefined => {
  if (!json) return undefined;
  const trimmed = json.trim();
  const tokenHoles = HOLE_LAYOUT_TOKENS[trimmed.toUpperCase()];
  if (tokenHoles) return tokenHoles;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (
      Array.isArray(parsed) &&
      parsed.every((n): n is number => typeof n === "number")
    ) {
      return parsed;
    }
  } catch {
    // Malformed / non-JSON value — fall back to no holes.
  }
  return undefined;
};

/**
 * Round conditions (pin, wind, sky, holes…) live on the rounds *list* DTO
 * (`getLeagueRounds`), not on the round *detail* DTO (`getLeagueRound`), so the
 * edit dialog can't pre-fill them from the detail alone. Pull the matching
 * round's settings out of the already-loaded list to seed the edit form —
 * `Clubhouse_RoundSetting` is field-for-field the form's settings shape.
 * The list keys rounds by numeric `id`; the detail exposes a stringified
 * `roundId`, so compare as strings.
 *
 * If the round's holes come back only as `selectedHolesJson` (not
 * `settings.selectedHoles`), fold them in so the holes preset still pre-fills.
 */
export const roundSettingsById = (
  rounds: Clubhouse_LeagueRoundV2Dto[] | undefined,
  roundId: string | undefined,
): (Clubhouse_RoundSetting & { selectedHoles?: number[] }) | undefined => {
  if (!roundId) return undefined;
  const round = rounds?.find((r) => String(r.id) === roundId);
  if (!round) return undefined;
  const settings = round.settings;
  // Holes are never carried inside settings on the wire — they live at the top
  // level as `selectedHolesJson`. Fold them into the form's convenience
  // `selectedHoles` field so the holes preset pre-fills on edit.
  const selectedHoles = parseSelectedHolesJson(round.selectedHolesJson);
  if (!settings && !selectedHoles) return undefined;
  return { ...settings, ...(selectedHoles ? { selectedHoles } : {}) };
};

export const leagueTypeLabel: Record<Clubhouse_LeagueType, string> = {
  [Clubhouse_LeagueType.TEAM]: "Team",
  [Clubhouse_LeagueType.INDIVIDUAL]: "Individual",
  [Clubhouse_LeagueType.SCRAMBLE]: "Scramble",
};

export const scoringLabel: Record<Clubhouse_LeagueScoring, string> = {
  [Clubhouse_LeagueScoring.STROKE]: "Stroke Play",
  [Clubhouse_LeagueScoring.MATCH]: "Match Play",
  [Clubhouse_LeagueScoring.POINTS]: "Points League",
};

/** Operator-facing "Individual · Stroke Play" line for league/round headers. */
export function formatLeagueCompetitionLabel(
  leagueType?: Clubhouse_LeagueType,
  scoring?: Clubhouse_LeagueScoring,
): string | undefined {
  if (leagueType == null) {
    return undefined;
  }

  return `${leagueTypeLabel[leagueType]} · ${scoringLabel[scoring ?? Clubhouse_LeagueScoring.STROKE]}`;
}

export const statusLabel: Record<Clubhouse_LeagueV2Status, string> = {
  [Clubhouse_LeagueV2Status.DRAFT]: "Draft",
  [Clubhouse_LeagueV2Status.ACTIVE]: "Active",
  [Clubhouse_LeagueV2Status.ARCHIVED]: "Archived",
};

/**
 * Operator-facing lifecycle for a league, derived from its raw status plus
 * round progress (see the LeagueOperatorLifecycleStatus ticket):
 *   DRAFT        — league is still a draft
 *   OPEN         — active, rounds scheduled but none currently open for play
 *   IN_PROGRESS  — at least one round is open / in progress
 *   COMPLETED    — archived, or every round is finalized (season complete)
 */
export type LeagueOperatorLifecycleStatus =
  | "DRAFT"
  | "OPEN"
  | "IN_PROGRESS"
  | "COMPLETED";

export const lifecycleLabel: Record<LeagueOperatorLifecycleStatus, string> = {
  DRAFT: "Draft",
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
};

export function getLeagueLifecycle(
  status: Clubhouse_LeagueV2Status | undefined,
  summary:
    | Pick<
        Clubhouse_LeagueOperatorDashboardStatusSummaryDto,
        "roundsInProgress" | "roundsFinalized" | "totalRounds"
      >
    | undefined,
): LeagueOperatorLifecycleStatus {
  switch (status) {
    case Clubhouse_LeagueV2Status.DRAFT:
      return "DRAFT";
    case Clubhouse_LeagueV2Status.ARCHIVED:
      return "COMPLETED";
    case Clubhouse_LeagueV2Status.ACTIVE:
    // Missing status is treated as an active league (best-effort).
    case undefined: {
      const total = summary?.totalRounds ?? 0;
      const finalized = summary?.roundsFinalized ?? 0;
      const inProgress = summary?.roundsInProgress ?? 0;
      // Every round finalized → the season is complete (the list must show this
      // even before the league auto-archives; using roundsInProgress alone would
      // wrongly read as "Open" once the last live round closes).
      if (total > 0 && finalized >= total) return "COMPLETED";
      // A live round is being played.
      if (inProgress > 0) return "IN_PROGRESS";
      // Rounds scheduled but none currently open (or not started yet).
      return "OPEN";
    }
    default:
      // Exhaustiveness guard: adding a Clubhouse_LeagueV2Status without
      // mapping it here is a compile error, so nothing silently falls
      // through. The runtime fallback keeps the dashboard resilient.
      status satisfies never;
      return "OPEN";
  }
}

// Muted, brand-aligned badge tones (softer than the bright *-50 fills):
// translucent tint + low-opacity border + a calmer, deeper text color, in both
// themes. Shared so all status/scorecard badges read as one system.
const BADGE_POSITIVE = "text-good border-good/25 bg-good-soft";
const BADGE_ATTENTION = "text-warn border-warn/30 bg-warn-soft";
const BADGE_INFO = "text-sky border-sky/25 bg-sky-soft";

/** Test-only accessor so badge class strings are pinned without exporting the raw constants for production use. */
export function getBadgeClassesForTest(
  kind: "positive" | "attention" | "info",
): string {
  return {
    positive: BADGE_POSITIVE,
    attention: BADGE_ATTENTION,
    info: BADGE_INFO,
  }[kind];
}

// Status pills. One palette across both levels: a round's own lifecycle and a
// participant's status within it use the same blue/gold/green, because they
// never appear side by side and mean the same thing one scope down. Every pill
// carries a border in its own text colour, so the outline reads as part of the
// pill rather than as a generic container edge. Note the tinted pills differ
// only by hue — the text label, always rendered, is what carries the state for
// anyone who can't separate them. Withdrawn is the exception that also changes
// shape: muted with a dashed outline, so a player who isn't playing reads as
// removed rather than as another live state.
const PILL_POSITIVE =
  "border-green-800/45 bg-green-600/12 text-green-800 dark:border-green-300/45 dark:bg-green-500/15 dark:text-green-300";
const PILL_ATTENTION =
  "border-amber-800/45 bg-amber-500/10 text-amber-800 dark:border-amber-300/45 dark:bg-amber-500/12 dark:text-amber-300";
const PILL_DANGER =
  "border-red-700/45 bg-red-500/10 text-red-700 dark:border-red-300/45 dark:bg-red-500/12 dark:text-red-300";
const PILL_INFO =
  "border-blue-800/45 bg-blue-500/12 text-blue-800 dark:border-blue-300/45 dark:bg-blue-500/15 dark:text-blue-300";
const PILL_MUTED_DASHED =
  "border-dashed border-text-muted/60 bg-transparent text-text-muted";
const PILL_NEUTRAL_SOLID = "border-text-muted/45 bg-muted text-text-muted";
const PILL_NEUTRAL_OUTLINE =
  "border-text-muted/45 bg-transparent text-text-muted";

/**
 * Display label for a round's lifecycle status. Every value keeps the API's own
 * word; only "InProgress" is split for readability.
 *
 * Note the Round status legend proposes showing Finalized as "Completed". That
 * is NOT applied: product confirmed the two are different states and both are
 * live, so relabelling would give them the same word. Don't collapse them.
 */
/**
 * The status an operator sees, which is not always the status the API stores.
 * `Finalized` is an internal lifecycle step — scored and locked — and product
 * shows it as Completed, because the distinction belongs to the scoring engine
 * rather than to someone reading a round (round status legend, CLUB-2441).
 */
export function roundStatusLabel(
  status: Clubhouse_LgRoundStatusV2 | undefined,
): string {
  const value = status ?? Clubhouse_LgRoundStatusV2.Scheduled;
  if (value === Clubhouse_LgRoundStatusV2.InProgress) return "In Progress";
  if (value === Clubhouse_LgRoundStatusV2.Finalized) return "Completed";
  return value;
}

/**
 * Round lifecycle colours: blue / gold / green, per the Round status legend.
 * All five enum values are live (confirmed with product), and the legend's
 * three colours cover them in pairs — Scheduled and Open are both "ready, not
 * started", Completed and Finalized are both "over". Sharing a colour is
 * deliberate; sharing a *label* is not — see roundStatusLabel.
 */
export const roundStatusClass: Record<Clubhouse_LgRoundStatusV2, string> = {
  [Clubhouse_LgRoundStatusV2.Scheduled]: PILL_INFO,
  [Clubhouse_LgRoundStatusV2.Open]: PILL_INFO,
  [Clubhouse_LgRoundStatusV2.InProgress]: PILL_ATTENTION,
  [Clubhouse_LgRoundStatusV2.Completed]: PILL_POSITIVE,
  [Clubhouse_LgRoundStatusV2.Finalized]: PILL_POSITIVE,
};

export const PARTICIPATION_LABEL: Record<
  Clubhouse_LgRoundParticipationStatusV2,
  string
> = {
  [Clubhouse_LgRoundParticipationStatusV2.Scheduled]: "Scheduled",
  [Clubhouse_LgRoundParticipationStatusV2.Withdrawn]: "Withdrawn",
  [Clubhouse_LgRoundParticipationStatusV2.InProgress]: "In Progress",
  [Clubhouse_LgRoundParticipationStatusV2.Played]: "Completed",
};

export const PARTICIPATION_CLASS: Record<
  Clubhouse_LgRoundParticipationStatusV2,
  string
> = {
  [Clubhouse_LgRoundParticipationStatusV2.Scheduled]: PILL_INFO,
  [Clubhouse_LgRoundParticipationStatusV2.Withdrawn]: PILL_MUTED_DASHED,
  [Clubhouse_LgRoundParticipationStatusV2.InProgress]: PILL_ATTENTION,
  [Clubhouse_LgRoundParticipationStatusV2.Played]: PILL_POSITIVE,
};

export const SCORECARD_LABEL: Record<
  Clubhouse_LgRoundScorecardStatusV2,
  string
> = {
  [Clubhouse_LgRoundScorecardStatusV2.None]: "Not yet played",
  [Clubhouse_LgRoundScorecardStatusV2.Pending]: "Pending",
  [Clubhouse_LgRoundScorecardStatusV2.Unlinked]: "Unlinked",
  [Clubhouse_LgRoundScorecardStatusV2.Linked]: "Linked",
  [Clubhouse_LgRoundScorecardStatusV2.ManualReviewRequired]: "Needs review",
};

export const SCORECARD_CLASS: Record<
  Clubhouse_LgRoundScorecardStatusV2,
  string
> = {
  [Clubhouse_LgRoundScorecardStatusV2.None]: PILL_NEUTRAL_OUTLINE,
  [Clubhouse_LgRoundScorecardStatusV2.Pending]: PILL_NEUTRAL_SOLID,
  [Clubhouse_LgRoundScorecardStatusV2.Unlinked]: PILL_DANGER,
  [Clubhouse_LgRoundScorecardStatusV2.Linked]: PILL_POSITIVE,
  [Clubhouse_LgRoundScorecardStatusV2.ManualReviewRequired]: PILL_ATTENTION,
};

/**
 * Where a card's scores came from, as its own pill beside the linkage status.
 * The two answer different questions: the status says whether a savegame is
 * attached, the source says who wrote the numbers.
 *
 * Linked is deliberately absent. A card no operator has touched is already
 * described by its status pill, and a second pill repeating it would say
 * nothing. Wording matches `linkedScorecardLine`, so the pill and the
 * edit-mode subline name the same thing.
 */
export const SCORECARD_SOURCE_LABEL: Partial<
  Record<Clubhouse_LgScorecardSourceV2, string>
> = {
  [Clubhouse_LgScorecardSourceV2.Edited]: "Edited",
  [Clubhouse_LgScorecardSourceV2.Manual]: "Entered by hand",
};

export const SCORECARD_SOURCE_CLASS: Partial<
  Record<Clubhouse_LgScorecardSourceV2, string>
> = {
  [Clubhouse_LgScorecardSourceV2.Edited]: PILL_INFO,
  [Clubhouse_LgScorecardSourceV2.Manual]: PILL_INFO,
};

/**
 * The waiting list's own pill, which answers only "has this player started?".
 *
 * Deliberately not SCORECARD_LABEL: the linkage states behind a waiting row
 * (Unlinked, ManualReviewRequired) are plumbing an operator resolves through
 * the Scorecard window, and naming them here puts a problem in front of
 * someone whose round is simply still being played. Green is reserved for a
 * card that is in, so a player mid-round reads as in motion, not as done.
 */
export const waitingRowPill = (
  status: Clubhouse_LgRoundScorecardStatusV2 | undefined,
): { label: string; className: string } =>
  status === Clubhouse_LgRoundScorecardStatusV2.Pending
    ? { label: "Playing now", className: PILL_ATTENTION }
    : { label: "Not yet played", className: PILL_INFO };

const BADGE_NEUTRAL = "border-border text-text-muted";
export const LIFECYCLE_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  DRAFT: { label: "Draft", className: BADGE_NEUTRAL },
  "0": { label: "Draft", className: BADGE_NEUTRAL },
  OPEN: { label: "Open", className: BADGE_INFO },
  "1": { label: "Open", className: BADGE_INFO },
  IN_PROGRESS: { label: "In Progress", className: BADGE_ATTENTION },
  "2": { label: "In Progress", className: BADGE_ATTENTION },
  COMPLETED: { label: "Completed", className: BADGE_POSITIVE },
  "3": { label: "Completed", className: BADGE_POSITIVE },
  ACTIVE: { label: "Active", className: BADGE_POSITIVE },
  ARCHIVED: { label: "Archived", className: BADGE_NEUTRAL },
};

/**
 * Parse a `*Utc` value as the real UTC instant it represents.
 *
 * These fields frequently arrive WITHOUT a `Z`, so a plain fromISO would read
 * them in the viewer's zone and shift the instant by the local offset (a season
 * start of `2026-09-07T05:00:00` — midnight Central — would land at 5am local).
 * Bare `yyyy-MM-dd` values carry no time and are genuine calendar dates, so
 * they're parsed locally, giving the start of that local day. Same contract as
 * formatDate.
 */
const parseUtcInstant = (value: string): DateTime =>
  value.includes("T")
    ? DateTime.fromISO(value, { zone: "utc" })
    : DateTime.fromISO(value);

/**
 * A league may be edited while it is still a DRAFT, or while ACTIVE but not yet
 * started (season start date in the future). Started ACTIVE leagues and ARCHIVED
 * leagues are read-only. An ACTIVE league with no known start date is treated as
 * not editable (we can't confirm it hasn't started).
 */
export const isLeagueEditable = (
  status: Clubhouse_LeagueV2Status | undefined,
  startDateUtc: string | Date | undefined,
): boolean => {
  if (status === Clubhouse_LeagueV2Status.DRAFT) return true;
  if (status === Clubhouse_LeagueV2Status.ACTIVE) {
    if (!startDateUtc) return false;
    const start =
      typeof startDateUtc === "string"
        ? parseUtcInstant(startDateUtc)
        : DateTime.fromJSDate(startDateUtc);
    // Fail safe: an unparseable date can't confirm the league hasn't started.
    if (!start.isValid) return false;
    return start > DateTime.now();
  }
  return false;
};

// A round is only editable while it is Scheduled (Open/InProgress/Completed/
// Finalized are locked) and its play window has not yet ended. Mirrors the API's
// "scheduled and not scored" rule as closely as the row data allows (the row
// carries no scored flag, so scoring is enforced server-side).
/**
 * @param isFlex A Season Flex round enforces no play window, so its window end
 *   must not gate editing. Such a round can still carry a stale window from
 *   before the league became flex, and since that date is no longer displayed
 *   anywhere, letting it disable the Edit action would leave the operator with
 *   a dead control and nothing on screen explaining why.
 */
const playWindowBoundaryInstant = (
  value: string | undefined,
  boundary: keyof typeof BOUNDARY_SECONDS,
  timeZone?: string,
): DateTime | null => {
  if (!value) return null;
  const isLegacyEndMarker =
    boundary === "end" && /T00:00:00(?:\.000)?Z?$/.test(value);
  if (isLegacyEndMarker) {
    const end = DateTime.fromISO(value, { zone: "utc" }).endOf("day");
    return end.isValid ? end : null;
  }
  return facilityBoundaryDate(value, boundary, timeZone);
};

/**
 * Whether the current instant falls inside the effective play window (assemble,
 * operator "open" copy). Mirrors the API's send-to-simulator window check.
 */
export const isWithinPlayWindow = (
  playWindowStartUtc: string | undefined,
  playWindowEndUtc: string | undefined,
  options: {
    isFlex?: boolean;
    leagueStartUtc?: string;
    leagueEndUtc?: string;
    schedulingTimezone?: string;
    now?: DateTime;
  } = {},
): boolean => {
  const {
    isFlex = false,
    leagueStartUtc,
    leagueEndUtc,
    schedulingTimezone,
    now = DateTime.utc(),
  } = options;

  const start = isFlex
    ? playWindowBoundaryInstant(leagueStartUtc, "start", schedulingTimezone)
    : (playWindowBoundaryInstant(
        playWindowStartUtc,
        "start",
        schedulingTimezone,
      ) ??
      playWindowBoundaryInstant(leagueStartUtc, "start", schedulingTimezone));
  const end = isFlex
    ? playWindowBoundaryInstant(leagueEndUtc, "end", schedulingTimezone)
    : (playWindowBoundaryInstant(
        playWindowEndUtc,
        "end",
        schedulingTimezone,
      ) ??
      playWindowBoundaryInstant(leagueEndUtc, "end", schedulingTimezone));

  if (!start?.isValid || !end?.isValid) return false;
  return now >= start && now <= end;
}

/**
 * True once the effective play window's last instant is in the past.
 *
 * A missing or unparseable end is not treated as closed — that would mark a
 * league finished when the payload simply omitted the date. Flex uses the
 * league season end; a fixed round uses its own window, then the season.
 */
export const hasPlayWindowEnded = (
  playWindowEndUtc: string | undefined,
  options: {
    isFlex?: boolean;
    leagueEndUtc?: string;
    schedulingTimezone?: string;
    now?: DateTime;
  } = {},
): boolean => {
  const {
    isFlex = false,
    leagueEndUtc,
    schedulingTimezone,
    now = DateTime.utc(),
  } = options;
  const endValue = isFlex ? leagueEndUtc : (playWindowEndUtc ?? leagueEndUtc);
  const end = playWindowBoundaryInstant(endValue, "end", schedulingTimezone);
  if (!end?.isValid) return false;
  return now > end;
};

/** Open / In Progress stop meaning "playable" once the date window has closed. */
export function roundStatusPresentation(
  status: Clubhouse_LgRoundStatusV2 | undefined,
  windowEnded: boolean,
): { label: string; className: string } {
  const value = status ?? Clubhouse_LgRoundStatusV2.Scheduled;
  if (
    windowEnded &&
    (value === Clubhouse_LgRoundStatusV2.Open ||
      value === Clubhouse_LgRoundStatusV2.InProgress)
  ) {
    return { label: "Closed", className: "border-border text-text-muted" };
  }
  return { label: roundStatusLabel(value), className: roundStatusClass[value] };
}

export const isRoundEditable = (
  status: Clubhouse_LgRoundStatusV2 | undefined,
  playWindowEndUtc: string | undefined,
  isFlex = false,
): boolean => {
  if (status !== Clubhouse_LgRoundStatusV2.Scheduled) return false;
  if (!isFlex && playWindowEndUtc) {
    // Round windows are facility-boundary instants. The shared decoder also
    // accepts the legacy midnight-UTC date-marker shape for older records.
    const isLegacyMarker = /T00:00:00(?:\.000)?Z?$/.test(playWindowEndUtc);
    const end = isLegacyMarker
      ? DateTime.fromISO(playWindowEndUtc, { zone: "utc" }).endOf("day")
      : facilityBoundaryDate(playWindowEndUtc, "end");
    // Fail safe: an unparseable end date can't confirm the window is still open.
    if (!end?.isValid) return false;
    if (end <= DateTime.utc()) return false;
  }
  return true;
};

// Map the full-rounds endpoint's DTOs into the row shape roundsColumns consumes
// (id→roundId, name→displayName), sorted by round number. hasOpenAdjudication-
// Issues isn't returned yet — it's passed through so the Issues column lights up
// automatically once the API includes it.
export const toRoundRows = (
  rounds: Clubhouse_LeagueRoundV2Dto[] | undefined,
): Clubhouse_LeagueOperatorDetailRecentRoundDto[] =>
  [...(rounds ?? [])]
    .map((r) => ({
      roundId: r.id != null ? String(r.id) : undefined,
      roundNumber: r.roundNumber,
      displayName: r.name,
      courseName: r.courseName,
      scoringType: r.settings?.scoringType,
      playWindowStartUtc: r.playWindowStartUtc,
      playWindowEndUtc: r.playWindowEndUtc,
      status: r.status,
      hasOpenAdjudicationIssues: r.hasOpenAdjudicationIssues,
    }))
    .sort((a, b) => (a.roundNumber ?? 0) - (b.roundNumber ?? 0));

/**
 * Format a date for display as "MMM d, yyyy".
 *
 * CONTRACT: any value with a time component (contains "T") is treated as a
 * UTC instant and converted to local — do not pass a *local* datetime, or
 * it will be shifted by the local offset. Bare `yyyy-MM-dd` calendar dates
 * are parsed as-is (no shift). All current callers pass `*Utc` fields.
 */
export const formatDate = (value: string | undefined): string => {
  if (!value) return "—";
  // `*Utc` values carry a time component and are UTC instants — but often
  // arrive without a `Z`, so luxon would parse them in the local zone and
  // show the wrong calendar day (e.g. an end-of-day 23:59 stored as next-day
  // 03:59Z reads a day late). Force UTC parsing, then convert to local.
  // Bare `yyyy-MM-dd` values are already calendar dates — parse as-is so they
  // aren't shifted a day.
  const dt = value.includes("T")
    ? DateTime.fromISO(value, { zone: "utc" }).toLocal()
    : DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat("MMM d, yyyy") : "—";
};

export const formatPlayWindow = (start?: string, end?: string): string => {
  if (!start && !end) return "—";
  const s = facilityBoundaryDate(start, "start")?.toFormat("MMM d") ?? "—";
  const e = facilityBoundaryDate(end, "end")?.toFormat("MMM d") ?? "—";
  return `${s} – ${e}`;
};

/** Facility calendar date formatted as "MMM d, yyyy". */
export const formatUtcDate = (
  value: string | undefined,
  boundary: keyof typeof BOUNDARY_SECONDS = "start",
): string =>
  facilityBoundaryDate(value, boundary)?.toFormat("MMM d, yyyy") ?? "—";

/** Facility play window formatted as "MMM d – MMM d". */
export const formatUtcWindow = (start?: string, end?: string): string => {
  if (!start && !end) return "—";
  const s = facilityBoundaryDate(start, "start")?.toFormat("MMM d") ?? "—";
  const e = facilityBoundaryDate(end, "end")?.toFormat("MMM d") ?? "—";
  return `${s} – ${e}`;
};

/**
 * League SEASON window, e.g. "Sep 7 – Jan 6".
 *
 * Season dates are UTC instants pinned to the FACILITY's local day boundaries
 * (start 00:00:00, end 23:59:59) — not midnight-UTC markers like round play
 * windows, and not the viewer's local time. A Central facility's season ending
 * Jan 6 is stored `2027-01-07T05:59:59`; formatting that in UTC shows Jan 7,
 * and converting it to the VIEWER's zone shows Jan 7 to an operator in Madrid.
 * Both are the same off-by-one bug from different directions.
 *
 * The facility's timezone is not on the dashboard DTO today (nor on the
 * Facility entity), so when it isn't supplied we recover the facility's UTC
 * offset from the boundary itself: the offset is the shift that lands the
 * instant's time-of-day exactly on 00:00:00 or 23:59:59. That is determined
 * only modulo 24h, so a real offset and its ±24h twin (Hawaii -10:00 vs
 * Kiritimati +14:00) are indistinguishable; where both are plausible we take
 * the one nearer the viewer's own offset. The viewer's zone therefore only ever
 * breaks a genuine tie — it never overrides a uniquely determined result.
 *
 * Pass `timeZone` once the DTO carries an IANA facility zone; it is
 * authoritative and skips the inference entirely.
 */
const BOUNDARY_SECONDS = { start: 0, end: 23 * 3600 + 59 * 60 + 59 } as const;
const MINUTES_PER_DAY = 1440;
// Real-world UTC offsets span (-12:00, +14:00].
const MIN_OFFSET = -720;
const MAX_OFFSET = 840;

export function facilityBoundaryDate(
  value: string | undefined,
  boundary: keyof typeof BOUNDARY_SECONDS,
  timeZone?: string,
): DateTime | null {
  if (!value) return null;
  // A bare yyyy-MM-dd is already a calendar date — no boundary to decode.
  if (!value.includes("T")) {
    const plain = DateTime.fromISO(value);
    return plain.isValid ? plain : null;
  }
  const utc = DateTime.fromISO(value, { zone: "utc" });
  if (!utc.isValid) return null;
  // Older round records use an exact midnight-UTC date marker for the end
  // date. It is not a facility end boundary, so preserve its UTC date instead
  // of trying to infer an offset from 00:00:00 as if it were 23:59:59 local.
  if (
    boundary === "end" &&
    utc.hour === 0 &&
    utc.minute === 0 &&
    utc.second === 0 &&
    utc.millisecond === 0
  ) {
    return utc;
  }
  if (timeZone) {
    const zoned = utc.setZone(timeZone);
    return zoned.isValid ? zoned : null;
  }

  const utcSeconds = utc.hour * 3600 + utc.minute * 60 + utc.second;
  const base = Math.round((BOUNDARY_SECONDS[boundary] - utcSeconds) / 60);
  const candidates = [
    base - MINUTES_PER_DAY,
    base,
    base + MINUTES_PER_DAY,
  ].filter((o) => o > MIN_OFFSET && o <= MAX_OFFSET);
  if (candidates.length === 0) return utc;
  const viewerOffset = DateTime.local().offset;
  const offset = candidates.reduce((best, o) =>
    Math.abs(o - viewerOffset) < Math.abs(best - viewerOffset) ? o : best,
  );
  return utc.setZone(FixedOffsetZone.instance(offset));
}

/** Recover the facility calendar date from a UTC day-boundary instant. */
export const toFacilityDateOnly = (
  value: string | undefined,
  boundary: keyof typeof BOUNDARY_SECONDS,
  timeZone?: string,
): string | undefined =>
  facilityBoundaryDate(value, boundary, timeZone)?.toISODate() ??
  (value?.includes("T") ? value.slice(0, 10) : value);

export const formatSeasonWindow = (
  start?: string,
  end?: string,
  timeZone?: string,
): string => {
  if (!start && !end) return "—";
  const s =
    facilityBoundaryDate(start, "start", timeZone)?.toFormat("MMM d") ?? "—";
  const e =
    facilityBoundaryDate(end, "end", timeZone)?.toFormat("MMM d") ?? "—";
  return `${s} – ${e}`;
};

/**
 * Title-cases an enum member for display: RED -> "Red", VERY_DRY -> "Very Dry".
 */
export function prettify(key: string): string {
  return key
    .replace(/^TIME_/, "")
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export const TEE_UNASSIGNED_LABEL = "Unassigned";

/**
 * Every tee the roster can carry, in enum order. Derived from RosterTeeType
 * rather than a hand-kept map, so a member added server-side shows up in the
 * pickers without a second edit here.
 */
export const TEE_TYPE_OPTIONS: RosterTeeType[] = Object.values(RosterTeeType);

/**
 * Why Flex is ruled out, in the words the rest of the Leagues UI uses. The
 * backend states the same rule as LeagueV2CreateLeagueSettingsRules
 * .MatchPlaySeasonFlexMessage; this is the operator-facing phrasing, which no
 * longer says "Season Flex" because the schedule modes are named Fixed and Flex.
 */
export const MATCH_PLAY_SEASON_FLEX_MESSAGE =
  "Flex is not available for Match Play.";

/** Whether Season Flex is a valid schedule choice for the given scoring format. */
export const isSeasonFlexAllowed = (
  leagueScoringType: Clubhouse_LeagueScoring | undefined,
): boolean => leagueScoringType !== Clubhouse_LeagueScoring.MATCH;

/** Operator-facing copy when Season Flex is blocked; undefined when allowed. */
export const seasonFlexDisabledReason = (
  leagueScoringType: Clubhouse_LeagueScoring | undefined,
): string | undefined =>
  leagueScoringType === Clubhouse_LeagueScoring.MATCH
    ? MATCH_PLAY_SEASON_FLEX_MESSAGE
    : undefined;

/**
 * A Season Flex league schedules rounds across the season rather than to a
 * weekly cadence, so play windows are not enforced and are not shown.
 *
 * Compared against the enum member, never truthiness: scheduleMode is a string
 * enum, so every mode is truthy and `!!mode` would report a weekly league as
 * flex. An absent mode is treated as not-flex, which keeps the pre-flex
 * behaviour of showing play windows.
 */
export const isFlexLeague = (
  season: Clubhouse_LeagueV2DetailSeasonDto | undefined,
): boolean =>
  season?.scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex;

export const teeLabel = (teeType: RosterTeeType | null | undefined): string =>
  teeType == null ? TEE_UNASSIGNED_LABEL : prettify(teeType);

/**
 * Normalizes whatever the API hands back into the roster's string domain.
 *
 * A TypeScript numeric enum carries a reverse mapping, so indexing one with a
 * name returns a number rather than failing — a payload that disagrees with its
 * declared type used to slip through as a crash downstream instead of a type
 * error. Both shapes are absorbed here. `== null`, not truthiness: TeeType.RED
 * is 0.
 */
export const toRosterTeeType = (
  value: RosterTeeType | number | null | undefined,
): RosterTeeType | null => {
  if (value == null) return null;
  return typeof value === "number"
    ? ((TeeType[value] as RosterTeeType | undefined) ?? null)
    : value;
};

/**
 * The tee as the write side wants it: the roster reads back a RosterTeeType
 * name, but POST and PATCH take the numeric TeeType on `teeSetId`. The two
 * enums share member names, which is what makes the round trip safe — see the
 * compile-time parity assertion beside RosterTeeType.
 *
 * Three results, because the write contract has three states:
 *   a name    -> its id, set the tee
 *   null      -> null, a deliberate clear
 *   unmapped  -> undefined, omit the key and leave the stored tee alone
 *
 * That last case is the one worth the extra state. Folding it into `null` would
 * mean a tee this build cannot name gets silently erased by any save that
 * touched something else entirely — the enums are hand-maintained, so a name
 * added server-side reaches us before its numeric counterpart does.
 */
export const teeSetIdOf = (
  teeType: RosterTeeType | null | undefined,
): number | null | undefined => {
  // Returns the nullish value it was given rather than folding both into null:
  // `null` is the operator choosing Unassigned, `undefined` is "no tee stated",
  // and only the first should reach the server as a clear.
  if (teeType == null) return teeType;
  return TeeType[teeType] ?? undefined;
};

/**
 * Resolve a round's course to the numeric `Course.id` the write contracts want.
 *
 * Reads hand back a SluggedId — the backend converts it to an int on the way
 * in, but nothing converts on the way out, so the frontend is left holding an
 * opaque slug it can't decode. The round create/PATCH bodies and the course
 * picker both work in numeric `Course.id`, so `courseName` is the only bridge
 * between them. Returns 0 when the course can't be matched (no name, or the
 * facility course list hasn't loaded yet) — callers must treat 0 as "unknown"
 * and never send it as a course.
 */
export const resolveCourseId = (
  courseName: string | undefined,
  courses: { id: number; displayName: string }[],
): number =>
  courseName ? (courses.find((c) => c.displayName === courseName)?.id ?? 0) : 0;
